import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Button, Icon, Popconfirm, Radio, Spin, Tooltip, message } from 'antd';
import { validate as uuidValidate } from 'uuid';
import { BUSINESS_LOGIC_TAB } from '../translationValues';
import {
  BusinessLogicCancelButton,
  BusinessLogicContainer,
  BusinessLogicFooter,
  BusinessLogicHeader,
  BusinessLogicSaveButton,
  DisableContainer,
  LogicNameWrapper,
  RuleEditingContainer,
  TableEditContainer,
} from './styles';
import { AddIcon, ArrowRightIcon, EditIcon } from '../../../images/svgIcons';
import {
  EQUALS_OPERATORS,
  MATCHERS,
  OPERATOR_KEY,
  OPERATOR_SYMBOLS,
  OPERATOR_VALUES,
  STARTS_OR_CONTAINS_OPERATOR,
  getDefaultCondition,
  getDefaultExtraCondition,
  getDefaultRule,
  getDefaultVariable,
} from '.';
import { TableView } from './tableView';
import { TableDisplayBody, TableMainBody, TableResultBody } from './tableEdit';
import { TableHead } from './tableHead';
import { TableArrow, useTableArrow } from './tableArrow';
import {
  deleteBusinessLogic as deleteBusinessLogicApi,
  deleteEmailBusinessLogic,
  generateTextFromPrompt,
  updateBusinessLogic,
  updateEmailBusinessLogic,
} from '../../../utils/services/businessLogicApi';
import { BusinessLogic } from '../../../context/businessLogicContext';
import { deepCopy } from '../../../utils/javascript';
import {
  CONTEXT_KEYS,
  useArtboardContext,
} from '../../../context/complexArtboardContext';
import { COMPLEX_DOCUMENT_VERSION } from '../../../utils/complexDocumentConstants';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../../context/complexDocumentContext';
import { useSessionContext } from '../../../context/sessionContext';
import { ENTERPRISE_ENV } from '../../../utils/helper';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';

const RadioGroup = Radio.Group;

export const PurpleTooltip = props => {
  return <Tooltip {...props} overlayClassName="purple-tooltip"></Tooltip>;
};

const BusinessLogicTable = ({ businessLogic, t, isNewFlow = false, allBlocks = [], ...props }) => {
  const {
    diffBlocks = [],
    flowHints,
    globalVariables,
    updateFlowHints,
    saveBusinessLogic,
    isValidAndUniqueName,
    setCurrentBusinessLogic,
    saveName,
    isAddBusinessLogic,
    editable,
    removeBusinessLogic,
    isInteractiveDesign
  } = props;
  const { rules, name, variables, id, documentId, type } = businessLogic;
  const { deletedRules, setDeletedRules } = useContext(BusinessLogic);

  const [isEditMode, setEditMode] = useState(props.isEditMode);
  const [businessLogicName, setBusinessLogicName] = useState(name);
  const [variablesList, setVariablesList] = useState(deepCopy(variables));
  const [rulesList, setRulesList] = useState(deepCopy(rules));
  const [predicateTruth, setPredicateTruth] = useState(businessLogic.predicateTruth);
  const [saveStatus, setSaveStatus] = useState('');
  const { showTableArrow, setshowTableArrow, tableScrollRef } = useTableArrow();
  const [ruleDeleteIndex, setRuleDeleteIndex] = useState(0);
  const businessLogicNameRef = useRef(null);
  const nodeRef = useRef(null);
  const saveButtonRef = useRef(null);
  const isNewLogic = uuidValidate(id);

  const isElementLogic = type === 'element';
  const isFlowLogic = type === 'flow';
  const isBlockLogic = type === 'block';

  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, `businessLogicTable`);

  const isEmailSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(isEmailSelector);

  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);

  const elementMapSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS], []);
  const [existingGroupMap, store] = useDocumentContext(elementMapSelector);

  const flowMapSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC], []);
  const [existingFlowMap] = useDocumentContext(flowMapSelector);


  const getLatestBusinessLogic = () => {
    return {
      rules: rulesList,
      variables: variablesList,
      name: businessLogicName,
      predicateTruth: predicateTruth,
      type:isBlockLogic?'block':isFlowLogic?'flow':'element',
      id,
      documentId,
    };
  };

  useEffect(() => {
    if (isAddBusinessLogic) {
      businessLogicNameRef.current.focus();
    }
    if (setCurrentBusinessLogic) {
      const bl = getLatestBusinessLogic();
      setCurrentBusinessLogic(bl);
    }
  }, [rulesList, variablesList, businessLogicName, predicateTruth]);

  const deleteBusinessLogic = async () => {
    let flowId;
    if (businessLogic.type === 'flow') {
      flowId = businessLogic.rules[0].flowId;
    }
    let groupIds;
    if (businessLogic.type === 'element') {
      groupIds = businessLogic.rules[0].groupIDs;
    }
    const payload = { id: id };
    try {
      let deleteBusinessLogicApiFn = deleteBusinessLogicApi;
      if (isEmailTemplate) {
        deleteBusinessLogicApiFn = deleteEmailBusinessLogic;
      }
      const { statusCode, status } = await deleteBusinessLogicApiFn(payload);
      if (statusCode === 1807) {
        if (businessLogic.type === 'element') {
          groupIds.forEach(id => {
            if (existingGroupMap.has(id)) {
              const current = existingGroupMap.get(id);
              current.groupName = '';
              existingGroupMap.set(id, {
                ...current,
                isBLPresent: false
              });
            }
          });
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: existingGroupMap });
        }
        if (businessLogic.type === 'flow') {
          existingFlowMap.set(flowId, { isBLPresent: false, blId: undefined });
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: existingFlowMap });
        }
        removeBusinessLogic(payload);
      } else if (status === 500) {
        throw new Error();
      }
    } catch (e) {
      message.error(
        t(BUSINESS_LOGIC_TAB.DELETE_BL_ERROR.key, BUSINESS_LOGIC_TAB.DELETE_BL_ERROR.value),
      );
    }
  };

  const addRule = () => {
    const defaultConditions = variablesList.map(item => getDefaultCondition(item.variableId));
    const rule = getDefaultRule(defaultConditions);
    setRulesList([...rulesList, rule]);
    //scroll to newly added rule in the table.
    requestAnimationFrame(() => {
      if (rulesList.length >= 2) {
        const ruleHeader = document.getElementById(`rule-${rulesList.length + 1}`);
        ruleHeader.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  const deleteRule = index => {
    const ruleToBeDeleted = rulesList[index];
    setRulesList(rulesList.filter(item => item.id !== ruleToBeDeleted.id));
    if (ruleToBeDeleted.isDeleted === false) {
      ruleToBeDeleted.isDeleted = true;
      delete ruleToBeDeleted.isNew;
      const deletedRulesTemp = deletedRules[id] ? deletedRules[id] : [];
      setDeletedRules({ ...deletedRules, [id]: [...deletedRulesTemp, ruleToBeDeleted] });
    }
  };

  const addCondition = () => {
    const variable = getDefaultVariable();
    setVariablesList([...variablesList, variable]);
    setRulesList(
      rulesList.map(item => {
        item.conditions = [...item.conditions, getDefaultCondition()];
        return item;
      }),
    );
  };

  const deleteCondition = varIndex => {
    setVariablesList(variablesList.filter((varItem, index) => index !== varIndex));
    setRulesList(
      rulesList.map(rule => {
        rule.conditions = rule.conditions.filter((conItem, index) => {
          return varIndex !== index;
        });
        return rule;
      }),
    );
  };

  const addExtraCondition = operatorItem => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = [...conItem.extraConditions, getDefaultExtraCondition()];
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const deleteExtraCondition = (operatorItem, extraOperatorItem) => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = conItem.extraConditions.filter(
                item => item.id !== extraOperatorItem.id,
              );
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const updateRuleByKey = (value, rule, key = 'blockId') => {
    setRulesList(
      rulesList.map(item => {
        if (item.id === rule.id) {
          item[key] = value;
        }
        return item;
      }),
    );
  };

  const updateVariableWithKey = (value, variableItem, key = 'variableId') => {
    setVariablesList(
      variablesList.map(varItem => {
        if (varItem.variableId === variableItem.variableId) {
          varItem[key] = value;
        }
        return varItem;
      }),
    );
  };

  // TODO: Redo this logic if and when performance gets hit.
  const updateOperatorByKey = (value, operatorItem, key, extraItem = null) => {
    setRulesList(
      rulesList.map(item => {
        if (item.id === operatorItem.ruleId) {
          item.conditions = item.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              if (extraItem) {
                conItem.extraConditions = conItem.extraConditions.map(exItem => {
                  if (exItem.id === extraItem.id) {
                    exItem[key] = value;
                  }
                  return exItem;
                });
              } else {
                conItem[key] = value;
              }
            }
            return conItem;
          });
        }
        return item;
      }),
    );
  };

  const ruleRearrange = (startIndex, endIndex) => {
    const firstRule = rulesList[startIndex];
    const secondRule = rulesList[endIndex];
    const rules = rulesList.map((rule, index) => {
      if (index === startIndex) {
        return secondRule;
      } else if (index === endIndex) {
        return firstRule;
      }
      return rule;
    });
    setRulesList(rules);
  };

  const generateExpressions = (conditions, masterVariableId, prevConValid = false) => {
    let expression = '';
    let promptExpression = '';
    conditions.forEach((condition, index) => {
      const { extraConditions, variableId, operator, value } = condition;
      const isExtraCondition = !!(extraConditions && extraConditions.length);
      const varId = masterVariableId || variableId;
      if (value || operator === OPERATOR_VALUES.IS_EMPTY) {
        if (index !== 0 && !masterVariableId && prevConValid) {
          expression += ' && ';
          promptExpression += ' && ';
        }
        if (isExtraCondition) {
          expression += '(';
          promptExpression += '(';
        }
        if (masterVariableId) {
          if (!prevConValid) {
            expression += ' && (';
            promptExpression += ' && (';
          } else {
            expression += ' || ';
            promptExpression += ' || ';
          }
        }
        const variable = globalVariables?.find(variable => variable.id === varId);
        const variableId = variable?.id;
        const variableName = variable?.placeHolderText;
        if (EQUALS_OPERATORS.includes(operator)) {
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
        } else if (operator === OPERATOR_VALUES.EQUALS_ANY_OF) {
          const result = value.split(",").map(v => `'${v}'`).join(",");
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
        } else if(STARTS_OR_CONTAINS_OPERATOR.includes(operator)) { 
          if(operator === 'Does not contain'){
            expression += `!${variableId}.${OPERATOR_KEY[operator]}('${value}')`;
            promptExpression += `${variableName} Does not contain '${value}'`;
          } if(operator === 'Does not start with') {
            expression += `!${variableId}.startsWith('${value}')`;
            promptExpression += `${variableName} Does not start with '${value}'`;
          } else {
            expression += `${variableId}.${OPERATOR_KEY[operator]}('${value}')`;
            promptExpression += `${variableName} ${OPERATOR_KEY[operator]} '${value}'`;
          }
        } else if(operator === OPERATOR_VALUES.IS_EMPTY) {
            expression += `${variableId}.isBlank()`;
            promptExpression += `${variableName} Is Empty`;
        } else{
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} ${value}`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} ${value}`;
        }
        prevConValid = true;
      } else {
        prevConValid = false;
      }

      if (isExtraCondition) {
        const [extraExp, extPExp] = generateExpressions(extraConditions, variableId, prevConValid);
        if (extraExp) {
          expression += `${extraExp})`;
        }
        if (extPExp) {
          promptExpression += `${extPExp})`;
        }
      }
    });
    return [expression, promptExpression];
  };

  const processVariables = variables => {
    return variables.map(variable => {
      delete variable.isNew;
      return variable;
    });
  };

  const processConditions = conditions => {
    return conditions.map(condition => {
      delete condition.isNew;
      if(condition.operator === OPERATOR_VALUES.IS_EMPTY) {
        condition.value = ''
      }
      return condition;
    });
  };

  const processRules = async rules => {
    const promises = [];
    let orderNumber = 1;
    const isAiWritingEnabled = ENTERPRISE_ENV && organisationDetails ? organisationDetails.aiWriting : true
    rules = rules.map(rule => {
      // remove isNew flag before sending to API.
      delete rule.isNew;
      rule.conditions = processConditions(rule.conditions);

      //check whether the rule is uuid if it is, make it null.
      const isUuid = uuidValidate(rule.id);
      if (isUuid) {
        rule.ruleUuid = rule.id;
        rule.id = null;
      }

      // increment orderNumber
      rule.orderNumber = orderNumber;
      orderNumber += 1;

      //generate expression
      const [expression, promptExpression] = generateExpressions(rule.conditions);
      rule.expression = expression;
      const blockName = allBlocks.find(block => block.backendId === rule.blockId)?.blockName;

      // only make api call to fetch openAI prompt response when expression is available.
      // if not, return empty string from promise.
      if (promptExpression) {
        const prompt = promptExpression
          .replace(/\|\|/g, 'OR')
          .replace(/ && /g, ', ')
          .replace(/\(/g, '')
          .replace(/\)/g, '')
          .replace(/==/g, '=')
          .replace(/ in /g, ' = ')
          .replace(/'/g, '');
        if (isAiWritingEnabled) {
          const query = isElementLogic? `Write only the natural language 'if' clause for this condition: ${prompt}, starting with "When:", and end it with exactly 'then show: '. Do not add anything else.` : `Interpret a meaningful message and translate the following rules into a statement 
          in natural language starting with "When: ${prompt} Then: show ${blockName}"`;
          promises.push(generateTextFromExpression(query));
        } else {
          promises.push(Promise.resolve(`${prompt} then show ${blockName}`));
        }
      } else {
        promises.push(Promise.resolve(''));
      }
      return rule;
    });
    const generatedTextList = await Promise.allSettled(promises);
    rules = rules.map((rule, index) => {
      rule.ruleText = generatedTextList[index].value;
      return rule;
    });
    return rules;
  };

  const generateTextFromExpression = async prompt => {
    const response = await generateTextFromPrompt(prompt);
    if (response.statusCode === 2201) {
      let str = response.data.choices[0].text;
      str = new DOMParser().parseFromString(str, 'text/html').documentElement.textContent;
      let dataString = str.split('\n');
      dataString = dataString.join('');
      return dataString;
    }
    return '';
  };

  const validateRules = rules => {
    let isError = false;
    for (let i = 0; i < rules.length; i++) {
      if (isBlockLogic && !rules[i].blockId) {
        message.error(t(BUSINESS_LOGIC_TAB.EMPTY_RULE_ERROR_MESSAGE.key, BUSINESS_LOGIC_TAB.EMPTY_RULE_ERROR_MESSAGE.value, {value: i + 1}));
        isError = true;
      }
    }
    return isError;
  };

  const validateVariables = variables => {
    let isError = false;
    for (let i = 0; i < variables.length; i++) {
      if (uuidValidate(variables[i].variableId)) {
        message.error(t(BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.key, BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.value, {value: i + 1}));
        isError = true;
        break;
      }
    }
    return isError;
  };

  const validateName = () => {
    let isError = false;
    if (!isValidAndUniqueName(businessLogicName, id)) {
      message.error(
        t(BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.key, BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.value),
      );
      isError = true;
    }
    return isError;
  };

  const onSave = async () => {
    //validate before save
    if (validateRules(rulesList) || validateVariables(variablesList) || validateName()) {
      return;
    }
    setSaveStatus('saving');
    const variables = processVariables(variablesList);
    const rules = await processRules(rulesList);
    const deletedRulesTemp = deletedRules[id] ? deletedRules[id] : [];
    const newBusinessLogic = {
      ...businessLogic,
      name: businessLogicName,
      rules: [...rules, ...deletedRulesTemp],
      type: type,
      variables: variables,
      predicateTruth: predicateTruth,
      documentId,
    };
    const payload = {
      ...newBusinessLogic,
      id: uuidValidate(id) ? null : id,
    };
    try {
      let updateBusinessLogicFn = updateBusinessLogic;
      if (isEmailTemplate) {
        updateBusinessLogicFn = updateEmailBusinessLogic;
      }
      const { statusCode, data, status } = await updateBusinessLogicFn(payload);
      if (statusCode === 1801 || statusCode === 3101) {
        setSaveStatus('saved');
        setRulesList(data.rules);
        setVariablesList(data.variables);
        setDeletedRules({ ...deletedRules, [id]: [] });
        setTimeout(() => {
          setEditMode(false);
          setSaveStatus('');
          saveBusinessLogic(data);
        }, 2000);

        updateVersionStore({
          [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: {
            ...lifeCycle,
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date(),
          },
        });
      } else if (statusCode === 1800) {
        message.error(
          t(BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.key, BUSINESS_LOGIC_TAB.UNIQUE_NAME_ERROR.value),
        );
        setSaveStatus('');
      } else if (status === 500) {
        throw new Error();
      }
    } catch (e) {
      message.error(
        t(BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.key, BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.value),
      );
      setSaveStatus('');
    }
  };

  const onCancel = () => {
    setEditMode(false);
    if (!isNewLogic) {
      setRulesList(deepCopy(rules));
      setBusinessLogicName(name);
      setVariablesList(deepCopy(variables));
      setPredicateTruth(businessLogic.predicateTruth);
    } else {
      setCurrentBusinessLogic && setCurrentBusinessLogic(null);
    }
  };

  const onFormSubmit = event => {
    event.preventDefault();
    if (isAddBusinessLogic) {
      saveName(businessLogicName);
    }
  };

  const tableHeadData = [
    t(BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.key, BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.value),
    ...rulesList.map(
      (item, index) =>
        `${t(BUSINESS_LOGIC_TAB.RULE_LABEL.key, BUSINESS_LOGIC_TAB.RULE_LABEL.value)} ` +
        (index + 1),
    ),
  ];
  const tableBodyData = variablesList.map((variable, index) => [
    variable,
    ...rulesList.map(rule => ({
      ...rule.conditions[index],
      ruleId: rule.id,
    })),
  ]);

  const tableBodyResult = [
    t(BUSINESS_LOGIC_TAB.RESULT_LABEL.key, BUSINESS_LOGIC_TAB.RESULT_LABEL.value),
    ...rulesList.map(item => null),
  ];

  const tableBodyDisplay = [
    t(BUSINESS_LOGIC_TAB.DISPLAY_LABEL.key, BUSINESS_LOGIC_TAB.DISPLAY_LABEL.value),
    ...rulesList.map(item => item),
  ];

  // TODO: have to change once rules conditions index are fixed.
  const saveEnabled =
    !!rulesList[0]?.conditions[0]?.operator &&
    !!rulesList[0].conditions[0].value &&
    !!rulesList[0]?.blockId;

  const elementOrFlowSaveEnabled = !!rulesList[0]?.conditions[0]?.operator && (!!rulesList[0].conditions[0].value || rulesList[0].conditions[0].operator === OPERATOR_VALUES.IS_EMPTY);

  const editTableContainerClass = `table-container ${rulesList.length > 1 ? 'table-scroll' : ''}`;
  const disabled = saveStatus === 'saving';

  const getCancelButtonStyle = () => {
    const saveButtonWidth = saveButtonRef?.current?.children?.[0]?.offsetWidth || 0;
    const style = { right: isNewLogic ? '' : saveButtonWidth, transform: '' };
    if (isNewLogic) {
      style.transform = saveEnabled  ? `translateX(-${saveButtonWidth}px)` : 'translateX(0px)';
    } else {
      style.transform = (saveEnabled || elementOrFlowSaveEnabled) ? 'translateX(0px)' : `translateX(${saveButtonWidth}px)`;
    }
    return style;
  };

  return (
    <DisableContainer id={`businessLogic-${id}`} disabled={disabled} >
      <BusinessLogicHeader
        onSubmit={onFormSubmit}
        enableForm={isAddBusinessLogic}
        disabled={disabled}
        type={type}
      >
        {isAddBusinessLogic ? (
          <>
            <input
              value={businessLogicName}
              ref={businessLogicNameRef}
              onChange={event => setBusinessLogicName(event.target.value)}
              placeholder={t(
                BUSINESS_LOGIC_TAB.BL_NAME_PLACEHOLDER.key,
                BUSINESS_LOGIC_TAB.BL_NAME_PLACEHOLDER.value,
              )}
            />
            <Button onClick={() => saveName(businessLogicName)} shape="round">
              {t(BUSINESS_LOGIC_TAB.NEXT_TEXT.key, BUSINESS_LOGIC_TAB.NEXT_TEXT.value)}{' '}
              <Icon component={ArrowRightIcon} />
            </Button>
          </>
        ) : (
          <LogicNameWrapper>
            {isEditMode ? (
              <PurpleTooltip
                overlay={t(
                  BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME_TOOLTIP_TEXT.key,
                  BUSINESS_LOGIC_TAB.BUSINESS_LOGIC_NAME_TOOLTIP_TEXT.value,
                )}
                placement="top"
              >
                <input
                  value={businessLogicName}
                  onChange={event => setBusinessLogicName(event.target.value)}
                />
              </PurpleTooltip>
            ) : (
              <div>{businessLogicName}</div>
            )}
          </LogicNameWrapper>
        )}
        {editable && !isInteractiveDesign && !isEditMode && !isAddBusinessLogic && (
          <div className="edit-delete-container">
            <Tooltip
              title={t(
                BUSINESS_LOGIC_TAB.DELETE_BL_TOOLTIP.key,
                BUSINESS_LOGIC_TAB.DELETE_BL_TOOLTIP.value,
              )}
            >
              <Popconfirm
                title={t(
                  BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.key,
                  BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.value,
                )}
                okText={t(
                  BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.key,
                  BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.value,
                )}
                cancelText={t(
                  BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.key,
                  BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.value,
                )}
                onConfirm={() => deleteBusinessLogic()}
              >
                <Icon type="delete" />
              </Popconfirm>
            </Tooltip>
            <Tooltip
              title={t(
                BUSINESS_LOGIC_TAB.EDIT_BL_TOOLTIP.key,
                BUSINESS_LOGIC_TAB.EDIT_BL_TOOLTIP.value,
              )}
            >
              <Icon component={EditIcon} onClick={() => setEditMode(!isEditMode)} />
            </Tooltip>
          </div>
        )}
      </BusinessLogicHeader>
      {!isAddBusinessLogic && (
        <>
          <BusinessLogicSaveButton show={(saveEnabled || elementOrFlowSaveEnabled) && isEditMode} ref={saveButtonRef}>
            {saveStatus === 'saved' ? (
              <span>
                {t(
                  BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.key,
                  BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.value,
                )}
              </span>
            ) : (
              <>
                {saveStatus !== 'saving' ? (
                  <div onClick={onSave}>
                    {t(
                      BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.key,
                      BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.value,
                    )}
                  </div>
                ) : (
                  <Spin indicator={<Icon type="loading" />}></Spin>
                )}
              </>
            )}
          </BusinessLogicSaveButton>
          <BusinessLogicCancelButton
            saveEnabled={saveEnabled|| elementOrFlowSaveEnabled}
            newLogic={isNewLogic}
            show={isEditMode && saveStatus === ''}
          >
            <span onClick={onCancel} style={getCancelButtonStyle()}>{t(BUSINESS_LOGIC_TAB.CANCEL_BUTTON.key, BUSINESS_LOGIC_TAB.CANCEL_BUTTON.value)}</span>
          </BusinessLogicCancelButton>
        </>
      )}
      <BusinessLogicContainer
        ref={nodeRef}
        disabled={disabled}
        show={!isAddBusinessLogic}
        showEdit={isEditMode}
      >
        <RuleEditingContainer type={type}>
          <TableEditContainer
            ref={tableScrollRef}
            className={editTableContainerClass}
            onMouseEnter={() => setshowTableArrow(true)}
            onMouseLeave={() => setshowTableArrow(false)}
            show={isEditMode}
            isBlockLogic={isBlockLogic}
          >
            <table>
              <TableHead
                tableHeadData={tableHeadData}
                flowHints={flowHints}
                isNewFlow={isNewFlow}
                t={t}
                tableScrollRef={tableScrollRef}
                ruleDeleteIndex={ruleDeleteIndex}
                ruleRearrange={ruleRearrange}
                deleteRule={deleteRule}
                setRuleDeleteIndex={setRuleDeleteIndex}
              />
              <tbody>
                <TableMainBody
                  tableBodyData={tableBodyData}
                  variablesList={variablesList}
                  updateVariableWithKey={updateVariableWithKey}
                  updateOperatorByKey={updateOperatorByKey}
                  addExtraCondition={addExtraCondition}
                  deleteExtraCondition={deleteExtraCondition}
                  deleteCondition={deleteCondition}
                  setRuleDeleteIndex={setRuleDeleteIndex}
                  globalVariables={globalVariables}
                  t={t}
                  isNewFlow={isNewFlow}
                  flowHints={flowHints}
                  updateFlowHints={updateFlowHints}
                />
                {isBlockLogic && (
                  <>
                    <TableResultBody
                      tableBodyResult={tableBodyResult}
                      isNewFlow={isNewFlow}
                      setRuleDeleteIndex={setRuleDeleteIndex}
                    />
                    <TableDisplayBody
                      tableBodyDisplay={tableBodyDisplay}
                      updateRuleByKey={updateRuleByKey}
                      setRuleDeleteIndex={setRuleDeleteIndex}
                      isNewFlow={isNewFlow}
                      flowHints={flowHints}
                      updateFlowHints={updateFlowHints}
                      allBlocks={allBlocks}
                      diffBlocks={diffBlocks}
                      rulesList={rulesList}
                      t={t}
                    />
                  </>
                )}
              </tbody>
              {showTableArrow && rulesList.length > 2 ? (
                <TableArrow tableScrollRef={tableScrollRef} />
              ) : (
                <></>
              )}
            </table>
          </TableEditContainer>
          <TableView
            tableBodyData={tableBodyData}
            tableHeadData={tableHeadData}
            rulesList={rulesList}
            isBlockLogic={isBlockLogic}
            isElementLogic={isElementLogic}
            isFlowLogic={isFlowLogic}
            variablesList={variablesList}
            isEditMode={isEditMode}
            t={t}
            setRulesList={setRulesList}
            onSave={onSave}
          />
          {isEditMode ? (
            <BusinessLogicFooter>
              <div onClick={addCondition} className="new-condition">
                <Icon component={AddIcon} />{' '}
                <span>
                  {t(BUSINESS_LOGIC_TAB.NEW_CONDITION.key, BUSINESS_LOGIC_TAB.NEW_CONDITION.value)}
                </span>
              </div>
              {isBlockLogic &&
                <div className="predicate-truth-radio">
                  <RadioGroup
                    value={predicateTruth}
                    onChange={event => setPredicateTruth(event.target.value)}
                  >
                    <Radio value={MATCHERS.matchfirst}>
                      {t(BUSINESS_LOGIC_TAB.MATCH_FIRST.key, BUSINESS_LOGIC_TAB.MATCH_FIRST.value)}
                    </Radio>
                    <Radio value={MATCHERS.matchLast}>
                      {t(BUSINESS_LOGIC_TAB.MATCH_LAST.key, BUSINESS_LOGIC_TAB.MATCH_LAST.value)}
                    </Radio>
                    <Radio value={MATCHERS.matchAll}>
                      {t(BUSINESS_LOGIC_TAB.MATCH_ALL.key, BUSINESS_LOGIC_TAB.MATCH_ALL.value)}
                    </Radio>
                  </RadioGroup>
                  <PurpleTooltip
                    overlay={t(
                      BUSINESS_LOGIC_TAB.LEARN_MORE_RETURN.key,
                      BUSINESS_LOGIC_TAB.LEARN_MORE_RETURN.value,
                    )}
                  >
                    <Icon type="info-circle" />
                  </PurpleTooltip>
                </div>
              }
            </BusinessLogicFooter>
          ) : (
            <></>
          )}
        </RuleEditingContainer>
        {isEditMode && isBlockLogic ? (
          <PurpleTooltip
            overlay={t(
              BUSINESS_LOGIC_TAB.ADD_MORE_RULE.key,
              BUSINESS_LOGIC_TAB.ADD_MORE_RULE.value,
            )}
            placement="top"
          >
            <Icon component={AddIcon} className="add-icon" onClick={() => addRule()}></Icon>
          </PurpleTooltip>
        ) : (
          <></>
        )}
      </BusinessLogicContainer>
    </DisableContainer>
  );
};

export default BusinessLogicTable;
